/**
 * The alert feed as a hook: feeds each new frame to the notification tracker
 * and keeps the list, the persisted per-kind mutes and the loss-banner
 * acknowledgements. `AlertStack` renders the stack from it and
 * `NotificationsPanel` the banners, edge alerts and minimap pings, so a HUD
 * layout can place the stack anywhere without owning the feed. Call it once
 * per HUD (NotificationsPanel does) and pass the result to AlertStack.
 */

import { useEffect, useRef, useState } from 'react';
import type { Cell, SimState } from '@/game/sim';
import {
  ATTACK_ONGOING,
  addEvents,
  createNotificationTracker,
  dismiss,
  frameEvents,
  lossBanner,
  mutedKinds,
  type Notification,
  type NotificationKind,
  type UrgentAlert,
  unmuted,
  urgentAlerts,
} from '@/game/view/notifications';
import { useSimFrame } from './app/simFrame';
import { usePersisted } from './persisted';

export interface Alerts {
  /** Every entry in the feed, dismissed ones too (they stay hidden, see Notification.dismissed). */
  notifications: Notification[];
  /** Kinds the player muted (never an attack or a loss). */
  muted: ReadonlySet<NotificationKind>;
  setMutedList: (kinds: NotificationKind[]) => void;
  /** Closes one entry (an attack or loss stays, hidden). */
  dismissEntry: (id: number) => void;
  /** Attack entries hit within ATTACK_ONGOING s, and the latest of them. */
  ongoing: Notification[];
  latestAttack: Notification | null;
  /** The latest unacknowledged loss and how many others, for the banner. */
  loss: { latest: Notification; others: number } | null;
  /** Acknowledges every loss now listed (the banner's ✕ or click). */
  closeLossBanner: () => void;
  /** Dismisses the ongoing attacks (the attack banner's ✕). */
  dismissOngoing: () => void;
  /** Urgent entries (own things hit, cities lost, spies exposed), muted ones left out. */
  urgent: UrgentAlert[];
}

/** Takes the camera to an entry: an own city still standing is selected too (idle workers); otherwise its tile. */
export function alertFocus(
  state: SimState,
  n: Notification,
  onFocus: (pos: Cell) => void,
  onSelectCity: (id: string) => void
): void {
  if (n.cityId && state.buildings[n.cityId]) onSelectCity(n.cityId);
  else if (n.pos) onFocus(n.pos);
}

export function useAlerts(
  /** Sites the player cancelled (their removal isn't a loss); a live set, read each tick. */
  cancelled?: ReadonlySet<string>
): Alerts {
  const frame = useSimFrame();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  // The loss entries acknowledged by closing the banner: they stay on the
  // feed, and losses still folding into them (one rolling entry per area)
  // don't raise it again; a new loss entry does (ids are never reused).
  const [seenLosses, setSeenLosses] = useState<ReadonlySet<number>>(() => new Set());
  const [mutedList, setMutedList] = usePersisted<NotificationKind[]>('mutedAlerts', []);
  const idRef = useRef(0);
  const [tracker] = useState(createNotificationTracker);
  useEffect(() => {
    const events = frameEvents(tracker, frame, cancelled);
    setNotifications((list) => {
      const updated = addEvents(list, events, frame.view.elapsed, () => ++idRef.current);
      // Keep the same array when nothing changed, so React skips the render.
      const same = updated.length === list.length && updated.every((n, i) => n === list[i]);
      return same ? list : updated;
    });
  }, [tracker, frame, cancelled]);

  const now = frame.view.elapsed;
  const muted = mutedKinds(mutedList);
  const shown = notifications.filter((n) => !n.dismissed);
  const ongoing = shown.filter((n) => n.kind === 'attack' && now - n.lastTime <= ATTACK_ONGOING);
  const latestAttack = ongoing.reduce<Notification | null>(
    (a, n) => (a === null || n.lastTime > a.lastTime ? n : a),
    null
  );
  return {
    notifications,
    muted,
    setMutedList,
    dismissEntry: (id) => setNotifications((list) => dismiss(list, id)),
    ongoing,
    latestAttack,
    loss: lossBanner(shown.filter((n) => n.kind !== 'loss' || !seenLosses.has(n.id))),
    closeLossBanner: () =>
      setSeenLosses((seen) => {
        const next = new Set(seen);
        for (const n of notifications) if (n.kind === 'loss') next.add(n.id);
        return next;
      }),
    dismissOngoing: () =>
      setNotifications((list) => ongoing.reduce((l, n) => dismiss(l, n.id), list)),
    // Own things hit, cities lost, own spies exposed: the screen edge glows and
    // arrows point at the ones out of view (EdgeAlerts).
    urgent: urgentAlerts(unmuted(notifications, muted), now),
  };
}
